import { useMemo, useState, useCallback } from "react";
import { useTranslations } from "use-intl";
import { useQueryClient } from "@tanstack/react-query";
import { showError } from "@/lib/toast";
import { useWorkflowSteps, useWorkflowWebhooks } from "@/hooks/api/queries/workflows.queries";
import { useConnectors } from "@/hooks/api/queries/connectors.queries";
import { useUpdateWorkflowDefinition } from "@/hooks/api/mutations/workflows.mutations";
import { workflowKeys } from "@/hooks/api/keys/workflows.keys";
import WorkflowFlowView from "../flow/WorkflowFlowView";
import { WorkflowLiveRunStrip } from "../flow/WorkflowLiveRunStrip";
import { useWorkflowCanvasRunOverlay } from "../flow/use-workflow-canvas-run-overlay";
import { StepConfigDrawer } from "../agentic/step-config-drawer";
import { CostBudgetBanner } from "../cost/cost-budget-banner";
import { WorkflowMetricTiles } from "../workflow-metric-tiles";
import { WebhookTriggerSurface } from "../triggers/webhook-trigger-surface";
import {
  useAgenticModelOptions,
  useMCPToolOptions,
} from "../agentic/use-agentic-reference-options";
import type { Workflow, WorkflowStep } from "@/types/workflow.types";

interface OverviewTabProps {
  workflow: Workflow;
}

export default function OverviewTab({ workflow }: OverviewTabProps) {
  const t = useTranslations("workflows");
  const qc = useQueryClient();
  const { data: stepsData, isLoading: loadingSteps } = useWorkflowSteps(workflow.id);
  const { data: webhookData } = useWorkflowWebhooks(workflow.id);
  const { data: connectorsData } = useConnectors({ limit: 200 });
  const { labelById: modelLabels } = useAgenticModelOptions();
  const { labelById: mcpToolLabels } = useMCPToolOptions();
  const updateDefinition = useUpdateWorkflowDefinition();
  const liveOverlay = useWorkflowCanvasRunOverlay(workflow.id);

  const steps = useMemo(
    () => stepsData?.data || workflow.steps || [],
    [stepsData?.data, workflow.steps],
  );
  const [activeStep, setActiveStep] = useState<WorkflowStep | null>(null);
  const hasWebhookTrigger = (webhookData?.data?.length ?? 0) > 0;

  const connectorMap = useMemo(() => {
    const map: Record<string, { name: string; subtype: string }> = {};
    for (const c of connectorsData?.data || []) {
      map[c.id] = { name: c.name, subtype: c.subtype || "" };
    }
    return map;
  }, [connectorsData]);

  const referenceLabels = useMemo(
    () => ({ models: modelLabels, mcpTools: mcpToolLabels }),
    [modelLabels, mcpToolLabels],
  );

  // Persist drag-repositioned node positions.
  // 500ms debounce is enforced inside WorkflowFlowView (onStepPositionChange
  // fires only after idle window) so we call the mutation directly here.
  const handleStepPositionChange = useCallback(
    (updates: Array<{ id: string; uiPosition: { x: number; y: number } }>) => {
      // Build updated steps array merging new positions over current state.
      const updatedSteps = steps.map((s) => {
        const hit = updates.find((u) => u.id === s.id);
        if (!hit) return { id: s.id, uiPosition: s.uiPosition ?? {} };
        return { id: s.id, uiPosition: hit.uiPosition };
      });

      updateDefinition.mutate(
        { id: workflow.id, definition: { steps: updatedSteps } },
        {
          onSuccess: () => {
            qc.invalidateQueries({ queryKey: workflowKeys.steps(workflow.id) });
          },
          onError: (err) => {
            // Revert: invalidate to re-fetch server-canonical positions.
            qc.invalidateQueries({ queryKey: workflowKeys.steps(workflow.id) });
            showError((err as { message?: string }).message || t("drag.error"));
          },
        },
      );
    },
    [steps, workflow.id, updateDefinition, qc, t],
  );

  if (loadingSteps) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="size-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Cost budget banner — renders null in ok state; warn/blocked above canvas */}
      <CostBudgetBanner resourceType="workflow" resourceId={workflow.id} />

      <WorkflowLiveRunStrip workflowId={workflow.id} overlay={liveOverlay} />

      {/* Aggregate run metrics — sourced from /workflows/:id/stats */}
      <WorkflowMetricTiles workflowId={workflow.id} />

      <WebhookTriggerSurface workflow={workflow} />

      {/* Flow diagram */}
      <WorkflowFlowView
        steps={steps}
        executionMode={workflow.executionMode}
        startLabel={hasWebhookTrigger ? t("trigger_webhook.canvas_label") : undefined}
        connectorMap={connectorMap}
        referenceLabels={referenceLabels}
        liveOverlayByStepId={liveOverlay.stepOverlayById}
        onNodeClick={setActiveStep}
        onStepPositionChange={handleStepPositionChange}
        height="clamp(420px, calc(100vh - 300px), 760px)"
        minHeight="420px"
      />

      {/* Step config drawer — opens on node click */}
      <StepConfigDrawer
        step={activeStep}
        workflowId={workflow.id}
        onClose={() => setActiveStep(null)}
      />
    </div>
  );
}
